import { cache } from "react";
import api from "./api";
import { ApiRoutes } from "./routeResolver";

/* ── Types ──────────────────────────────────────────────────────── */

export interface DestinationInRegion {
  id: string;
  nameCountry: string;
  codeIso2Country: string;
  [key: string]: unknown;
}

export interface DestinationWithPackage {
  id: string;
  countryName: string;
  codeIso2Country?: string;
  [key: string]: unknown;
}

export interface TravelNetwork {
  id: string;
  name: string;
}

export interface HostAgency {
  id: string;
  name: string;
}

export interface MiniPackage {
  id: string;
  name?: string;
  nights?: number;
  [key: string]: unknown;
}

export interface WebsiteGuest {
  email: string;
  isPassenger: boolean;
  agentId: string | null;
  existingAgent: boolean;
}

/* ── Server-side fetchers (cached per request) ──────────────────── */

export const fetchDestinations = cache(async (): Promise<{
  destinations: DestinationInRegion[];
  destinationsWithPackages: DestinationWithPackage[];
}> => {
  const [destRes, destWithPkgRes] = await Promise.all([
    api
      .get(ApiRoutes.FOUNDATION_COUNTRIES_IN_REGIONS, {
        params: { CompanyId: process.env.NEXT_PUBLIC_COMPANY_ID },
      })
      .catch(() => null),
    api
      .get(ApiRoutes.INVENTORY_PACKAGE_COUNTRIES_WITH_PACKAGE, {
        params: { regoinId: null },
      })
      .catch(() => null),
  ]);

  const destinations: DestinationInRegion[] = (
    destRes?.data?.data ?? []
  ).sort((a: DestinationInRegion, b: DestinationInRegion) =>
    a.nameCountry.toUpperCase().localeCompare(b.nameCountry.toUpperCase())
  );

  const destinationsWithPackages: DestinationWithPackage[] = (
    destWithPkgRes?.data?.data ?? []
  ).sort((a: DestinationWithPackage, b: DestinationWithPackage) =>
    a.countryName.toUpperCase().localeCompare(b.countryName.toUpperCase())
  );

  return { destinations, destinationsWithPackages };
});

/* ── Website guest tracking (browser) — never throws ────────────── */

export async function registerWebsiteGuest(
  guest: WebsiteGuest,
): Promise<string | null> {
  const res = await api
    .post(ApiRoutes.FOUNDATION_WEBSITE_GUEST, {
      companyId: process.env.NEXT_PUBLIC_COMPANY_ID,
      ...guest,
    })
    .catch(() => null);
  return res?.data?.entityId ?? null;
}

export async function submitWebsiteGuestLead(id: string): Promise<void> {
  await api
    .put(
      ApiRoutes.FOUNDATION_WEBSITE_GUEST_SUBMIT_LEAD,
      { id },
      { timeout: 5000 },
    )
    .catch(() => {});
}
